import { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import {
  BrowserRouter,
  Link,
  NavLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Clock3,
  Filter,
  LockKeyhole,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  NotebookPen,
  Phone,
  Search,
  Shield,
  ShieldCheck,
  UserRound,
  Users,
  Briefcase,
  GraduationCap,
  Calendar,
  FileText,
  X,
} from "lucide-react";
import "./App.css";
import { API } from "@/config";
import { Seo } from "@/components/Seo";
import { formatWhatsappNumber, buildServiceDetails, extractApiError } from "@/utils/formatters";
import { LOCATIONS } from "@/data/locations";
import {
  CATEGORY_ORDER,
  DEFAULT_SITE_CONFIG,
  HERO_IMAGE,
  SECONDARY_IMAGE,
  BENEFIT_PILLS,
  VALUE_ITEMS,
  REFERENCE_SECTORS,
  WHY_US_POINTS,
  PROCESS_STEPS,
} from "@/data/content";
import { DatenschutzPage, ImpressumPage, AGBPage } from "@/pages/LegalPages";
import { CareerPage } from "@/pages/CareerPage";
import { SectionHeading } from "@/components/SectionHeading";


function ScrollManager() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const timeout = setTimeout(() => {
        const target = document.querySelector(location.hash);
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 120);
      return () => clearTimeout(timeout);
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
    return undefined;
  }, [location.pathname, location.hash]);

  return null;
}

function NavAnchor({ href, label, testId, onClick }) {
  return (
    <a href={href} className="nav-link" data-testid={testId} onClick={onClick}>
      {label}
    </a>
  );
}

function InquiryForm({ type, services, title, text, compact = false, preselectedService = "" }) {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    service: preselectedService || (type === "anfrage" ? services[0]?.title || "" : ""),
    message: "",
    consent: false,
  });
  const [status, setStatus] = useState({ loading: false, success: "", error: "" });

  useEffect(() => {
    if (preselectedService) {
      setFormData((current) => ({ ...current, service: preselectedService }));
    } else if (type === "anfrage" && !formData.service && services.length > 0) {
      setFormData((current) => ({ ...current, service: services[0].title }));
    }
  }, [type, services, preselectedService]);

  const handleChange = (event) => {
    const { name, value, type: inputType, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: inputType === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ loading: true, success: "", error: "" });

    try {
      await axios.post(`${API}/inquiries`, {
        type,
        name: formData.name,
        company: formData.company,
        phone: formData.phone,
        email: formData.email,
        service: type === "anfrage" ? formData.service : undefined,
        message: formData.message,
        consent: formData.consent,
      });

      setStatus({
        loading: false,
        success:
          type === "anfrage"
            ? "Ihre Anfrage wurde erfolgreich übermittelt. Wir melden uns zeitnah bei Ihnen zurück."
            : "Ihre Nachricht wurde erfolgreich übermittelt. Vielen Dank für Ihre Kontaktaufnahme.",
        error: "",
      });
      setFormData({
        name: "",
        company: "",
        phone: "",
        email: "",
        service: type === "anfrage" ? services[0]?.title || "" : "",
        message: "",
        consent: false,
      });
    } catch (error) {
      setStatus({
        loading: false,
        success: "",
        error: extractApiError(
          error,
          "Die Übermittlung konnte gerade nicht abgeschlossen werden. Bitte versuchen Sie es erneut."
        ),
      });
    }
  };

  return (
    <div className={`form-card ${compact ? "form-card--compact" : ""}`}>
      <div className="form-card__header">
        <span className="section-eyebrow">{type === "anfrage" ? "Anfrageformular" : "Kontaktformular"}</span>
        <h3>{title}</h3>
        <p>{text}</p>
      </div>

      <form onSubmit={handleSubmit} className="inquiry-form" data-testid={`${type}-form`}>
        <div className="form-grid two-column">
          <label>
            <span>Name *</span>
            <input
              data-testid={`${type}-name-input`}
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Vor- und Nachname"
              required
            />
          </label>
          <label>
            <span>Firma</span>
            <input
              data-testid={`${type}-company-input`}
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Unternehmen / Objekt"
            />
          </label>
        </div>

        <div className="form-grid two-column">
          <label>
            <span>Telefon *</span>
            <input
              data-testid={`${type}-phone-input`}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="0174..."
              required
            />
          </label>
          <label>
            <span>E-Mail *</span>
            <input
              data-testid={`${type}-email-input`}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="name@firma.de"
              required
            />
          </label>
        </div>

        {type === "anfrage" ? (
          <label>
            <span>Gewünschte Leistung *</span>
            <select
              data-testid="anfrage-service-select"
              name="service"
              value={formData.service}
              onChange={handleChange}
              required
            >
              {services.map((service) => (
                <option key={service.slug} value={service.title}>
                  {service.title}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label>
          <span>Nachricht *</span>
          <textarea
            data-testid={`${type}-message-input`}
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder={
              type === "anfrage"
                ? "Beschreiben Sie kurz Einsatzort, Zeitraum und gewünschten Umfang."
                : "Womit können wir Ihnen weiterhelfen?"
            }
            rows={compact ? 5 : 6}
            required
          />
        </label>

        <label className="checkbox-row">
          <input
            data-testid={`${type}-consent-checkbox`}
            type="checkbox"
            name="consent"
            checked={formData.consent}
            onChange={handleChange}
            required
          />
          <span>
            Ich stimme der Verarbeitung meiner Angaben zur Bearbeitung meiner Anfrage zu. *
          </span>
        </label>

        {status.success ? <div className="form-feedback success">{status.success}</div> : null}
        {status.error ? <div className="form-feedback error">{status.error}</div> : null}

        <button
          type="submit"
          className="button button--primary button--full"
          data-testid={`${type}-submit-button`}
          disabled={status.loading}
        >
          {status.loading ? "Wird gesendet..." : type === "anfrage" ? "Anfrage absenden" : "Nachricht senden"}
        </button>
      </form>
    </div>
  );
}

function ServiceOverviewCard({ category, items }) {
  return (
    <article className="category-card">
      <div className="category-card__header">
        <span className="section-eyebrow">Leistungen</span>
        <h3>{category}</h3>
      </div>
      <ul>
        {items.map((item) => (
          <li key={item.slug}>
            <Link to={`/leistungen/${item.slug}`} data-testid={`category-link-${item.slug}`}>
              <span>{item.title}</span>
              <ChevronRight size={18} />
            </Link>
          </li>
        ))}
      </ul>
      <Link to="/leistungen" className="text-link" data-testid={`more-${category}`}>
        Weitere Details <ArrowRight size={16} />
      </Link>
    </article>
  );
}

function ServiceCard({ service }) {
  return (
    <article className="service-card service-card--with-image" data-testid={`service-card-${service.slug}`}>
      {service.image && (
        <div className="service-card__image">
          <img src={service.image} alt={service.title} loading="lazy" />
        </div>
      )}
      <div className="service-card__content">
        <div className="service-card__top">
          <span className="service-card__category">{service.category}</span>
          <h3>{service.title}</h3>
        </div>
        <p>{service.summary}</p>
        <Link to={`/leistungen/${service.slug}`} className="text-link" data-testid={`service-detail-${service.slug}`}>
          Mehr erfahren <ArrowRight size={16} />
        </Link>
      </div>
    </article>
  );
}

function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, statistics: false, marketing: false });

  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      setTimeout(() => setShowBanner(true), 100);
    }
  }, []);

  const saveConsent = (value) => {
    localStorage.setItem("cookie_consent", JSON.stringify({ ...value, ts: new Date().toISOString() }));
    setShowBanner(false);
  };

  const handleAcceptAll = () => {
    saveConsent({ necessary: true, statistics: true, marketing: true });
  };

  const handleNecessaryOnly = () => {
    saveConsent({ necessary: true, statistics: false, marketing: false });
  };

  const handleSaveSettings = () => {
    saveConsent(prefs);
  };

  if (!showBanner) return null;

  return (
    <div className="cookie-overlay" data-testid="cookie-overlay">
      <div className="cookie-banner" data-testid="cookie-banner">
        {!showSettings ? (
          <div className="cookie-banner__content">
            <div className="cookie-banner__text">
              <strong>🍪 Cookie-Hinweis</strong>
              <p>
                Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten.
                Notwendige Cookies sind stets aktiv. Statistik- und Marketing-Cookies nutzen wir nur mit Ihrer Zustimmung.
                Weitere Informationen finden Sie in unserer <Link to="/datenschutz">Datenschutzerklärung</Link>.
              </p>
            </div>
            <div className="cookie-banner__actions cookie-banner__actions--three">
              <button onClick={handleNecessaryOnly} className="button button--secondary button--small" data-testid="cookie-necessary-btn">
                Nur notwendige
              </button>
              <button onClick={() => setShowSettings(true)} className="button button--outline button--small" data-testid="cookie-settings-btn">
                Einstellungen
              </button>
              <button onClick={handleAcceptAll} className="button button--primary button--small" data-testid="cookie-accept-all-btn">
                Alle akzeptieren
              </button>
            </div>
          </div>
        ) : (
          <div className="cookie-banner__content">
            <div className="cookie-banner__text">
              <strong>⚙️ Cookie-Einstellungen</strong>
              <p>Wählen Sie, welche Cookies Sie zulassen möchten.</p>
            </div>
            <div className="cookie-settings-list">
              <label className="cookie-option cookie-option--disabled">
                <input type="checkbox" checked readOnly data-testid="cookie-toggle-necessary" />
                <div>
                  <span className="cookie-option__title">Notwendige Cookies</span>
                  <span className="cookie-option__desc">Erforderlich für den Betrieb der Website (z. B. Session). Immer aktiv.</span>
                </div>
              </label>
              <label className="cookie-option">
                <input
                  type="checkbox"
                  checked={prefs.statistics}
                  onChange={(e) => setPrefs({ ...prefs, statistics: e.target.checked })}
                  data-testid="cookie-toggle-statistics"
                />
                <div>
                  <span className="cookie-option__title">Statistik</span>
                  <span className="cookie-option__desc">Anonyme Nutzungsdaten zur Verbesserung der Website.</span>
                </div>
              </label>
              <label className="cookie-option">
                <input
                  type="checkbox"
                  checked={prefs.marketing}
                  onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
                  data-testid="cookie-toggle-marketing"
                />
                <div>
                  <span className="cookie-option__title">Marketing</span>
                  <span className="cookie-option__desc">Personalisierte Inhalte und Werbeanzeigen.</span>
                </div>
              </label>
            </div>
            <div className="cookie-banner__actions">
              <button onClick={() => setShowSettings(false)} className="button button--secondary button--small" data-testid="cookie-back-btn">
                Zurück
              </button>
              <button onClick={handleSaveSettings} className="button button--primary button--small" data-testid="cookie-save-btn">
                Auswahl speichern
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PublicLayout({ siteConfig, groupedServices, allServices, children }) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const whatsappLink = `https://wa.me/${formatWhatsappNumber(siteConfig.contact.whatsapp)}`;
  const phoneLink = `tel:+49${siteConfig.contact.phone.replace(/^0/, "")}`;

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <>
      <header className="site-header">
        <div className="container site-header__inner">
          <Link to="/" className="brand" data-testid="brand-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_image-tracker-23/artifacts/iputb164_ChatGPT%20Image%2020.%20Apr.%202026%2C%2013_21_51.png" 
              alt="Deutsche B&S Logo" 
              className="brand__logo"
            />
            <div className="brand__text">
              <span className="brand__eyebrow">Deutsche</span>
              <span className="brand__name">B&S</span>
            </div>
          </Link>

          <nav className="desktop-nav">
            <NavAnchor href="/#start" label="Startseite" testId="nav-home" />
            <NavAnchor href="/#leistungen" label="Leistungen" testId="nav-services" />
            <NavAnchor href="/standorte" label="Standorte" testId="nav-locations" />
            <NavAnchor href="/ueber-uns" label="Über uns" testId="nav-about" />
            <NavAnchor href="/karriere" label="Karriere" testId="nav-career" />
            <NavAnchor href="/kontakt" label="Kontakt" testId="nav-contact" />
          </nav>

          <div className="site-header__actions">
            <a href={phoneLink} className="header-mini-link" data-testid="header-phone-link">
              <Phone size={16} /> {siteConfig.contact.phone}
            </a>
            <Link to="/anfrage" className="button button--primary" data-testid="header-cta-button">
              Jetzt anfragen
            </Link>
            <button
              type="button"
              className="menu-toggle"
              onClick={() => setMenuOpen((current) => !current)}
              data-testid="mobile-menu-toggle"
              aria-label="Menü öffnen"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {menuOpen ? (
          <div className="mobile-nav-panel">
            <NavAnchor href="/#start" label="Startseite" testId="mobile-nav-home" onClick={() => setMenuOpen(false)} />
            <NavAnchor href="/#leistungen" label="Leistungen" testId="mobile-nav-services" onClick={() => setMenuOpen(false)} />
            <NavAnchor href="/standorte" label="Standorte" testId="mobile-nav-locations" onClick={() => setMenuOpen(false)} />
            <NavAnchor href="/ueber-uns" label="Über uns" testId="mobile-nav-about" onClick={() => setMenuOpen(false)} />
            <NavAnchor href="/karriere" label="Karriere" testId="mobile-nav-career" onClick={() => setMenuOpen(false)} />
            <NavAnchor href="/kontakt" label="Kontakt" testId="mobile-nav-contact" onClick={() => setMenuOpen(false)} />
          </div>
        ) : null}
      </header>

      <main>{children}</main>

      <footer className="site-footer">
        <div className="container footer-grid">
          <div>
            <div className="brand brand--footer">
              <img 
                src="https://customer-assets.emergentagent.com/job_image-tracker-23/artifacts/iputb164_ChatGPT%20Image%2020.%20Apr.%202026%2C%2013_21_51.png" 
                alt="Deutsche B&S Logo" 
                className="brand__logo brand__logo--footer"
              />
              <div className="brand__text">
                <span className="brand__eyebrow">Deutsche</span>
                <span className="brand__name">B&S</span>
              </div>
            </div>
            <p className="footer-copy">
              Deutsche Bewachung & Sicherheit – moderne Sicherheitslösungen für Unternehmen, Baustellen,
              Veranstaltungen und sensible Einsatzbereiche in Hannover und Niedersachsen.
            </p>
          </div>
          <div>
            <h4>Kontakt</h4>
            <ul className="footer-list">
              <li>
                <MapPin size={16} />
                <span>
                  {siteConfig.contact.address}, {siteConfig.contact.city}
                </span>
              </li>
              <li>
                <Phone size={16} />
                <a href={phoneLink} data-testid="footer-phone-link">
                  {siteConfig.contact.phone}
                </a>
              </li>
              <li>
                <Mail size={16} />
                <a href={`mailto:${siteConfig.contact.email}`} data-testid="footer-email-link">
                  {siteConfig.contact.email}
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4>Schnellzugriff</h4>
            <ul className="footer-links">
              <li>
                <a href="/#leistungen" data-testid="footer-services-link">
                  Leistungen
                </a>
              </li>
              <li>
                <Link to="/ueber-uns" data-testid="footer-about-link">
                  Über uns
                </Link>
              </li>
              <li>
                <Link to="/anfrage" data-testid="footer-request-link">
                  Anfrage senden
                </Link>
              </li>
              <li>
                <Link to="/kontakt" data-testid="footer-contact-link">
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4>Rechtliches</h4>
            <ul className="footer-links">
              <li>
                <Link to="/impressum">Impressum</Link>
              </li>
              <li>
                <Link to="/datenschutz">Datenschutz</Link>
              </li>
              <li>
                <Link to="/agb">AGB</Link>
              </li>
            </ul>
          </div>
        </div>
      </footer>

      <div className="floating-actions">
        <a href={phoneLink} className="floating-action floating-action--phone" data-testid="floating-phone-button" aria-label="Anrufen">
          <Phone size={20} />
          <span>Anrufen</span>
        </a>
        <a
          href={whatsappLink}
          className="floating-action floating-action--whatsapp"
          target="_blank"
          rel="noreferrer"
          data-testid="floating-whatsapp-button"
          aria-label="WhatsApp"
        >
          <MessageCircle size={20} />
          <span>WhatsApp</span>
        </a>
        <a
          href={`mailto:${siteConfig.contact.email}`}
          className="floating-action floating-action--email"
          data-testid="floating-email-button"
          aria-label="E-Mail"
        >
          <Mail size={20} />
          <span>E-Mail</span>
        </a>
      </div>

      <CookieBanner />
    </>
  );
}

function HomePage({ siteConfig, groupedServices, allServices }) {
  const featureServices = allServices.slice(0, 6);

  return (
    <>
      <Seo
        path="/"
        title={null}
        description="Deutsche Bewachung & Sicherheit – Sicherheitsdienst in Hannover, Bremen, Hamburg, Braunschweig & Wolfsburg. Objektschutz, Revierdienst, Baustellen- und Veranstaltungssicherheit aus einer Hand."
        keywords="Sicherheitsdienst Hannover, Sicherheitsfirma Niedersachsen, Objektschutz, Revierdienst, Veranstaltungssicherheit, Baustellenbewachung, Werkschutz, Deutsche Bewachung und Sicherheit"
      />
      <section className="hero-section" id="start">
        <div className="container hero-grid">
          <div className="hero-copy">
            <span className="section-eyebrow">Sicherheitsdienst in Hannover</span>
            <h1>Klare Sicherheitslösungen für Unternehmen, Baustellen, Events und sensible Einsatzorte.</h1>
            <p>
              {siteConfig.brand.name} entwickelt einsatznahe Sicherheitsleistungen mit professioneller Präsenz,
              strukturierten Abläufen und direkter Erreichbarkeit.
            </p>
            <div className="hero-actions">
              <Link to="/anfrage" className="button button--primary" data-testid="hero-request-button">
                Jetzt anfragen
              </Link>
            </div>
            <div className="hero-pills">
              {BENEFIT_PILLS.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-visual__card hero-visual__card--large">
              <img src={HERO_IMAGE} alt="Sicherheitsdienst an einem Gebäudeeingang" />
            </div>
            <div className="hero-visual__info">
              <div className="signal-card signal-card--dark">
                <ShieldCheck size={22} />
                <div>
                  <strong>Präsenz & Schutz</strong>
                  <span>sichtbar, diskret und einsatzorientiert</span>
                </div>
              </div>
              <div className="hero-visual__card hero-visual__card--small">
                <img src={SECONDARY_IMAGE} alt="Sicherheitskraft vor einem Objekt" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="signal-strip customers-strip">
        <div className="container">
          <div className="customers-header">
            <span className="section-eyebrow">Vertrauen seit Jahren</span>
            <h2>Unsere Kunden</h2>
            <p>Unternehmen und Auftraggeber, die auf Deutsche B&S setzen.</p>
          </div>
          <div className="customer-logos-grid">
            <div className="customer-logo">
              <img src="https://customer-assets.emergentagent.com/job_image-tracker-23/artifacts/6fixc8p1_Screenshot%202026-04-09%20153419.png" alt="Region Hannover" />
            </div>
            <div className="customer-logo">
              <img src="https://customer-assets.emergentagent.com/job_image-tracker-23/artifacts/ydl7y42y_Screenshot%202026-04-09%20153336.png" alt="Landeshauptstadt Hannover" />
            </div>
            <div className="customer-logo">
              <img src="https://customer-assets.emergentagent.com/job_image-tracker-23/artifacts/8mmrfdk7_Screenshot%202026-04-09%20153513.png" alt="DHL" />
            </div>
            <div className="customer-logo">
              <img src="https://customer-assets.emergentagent.com/job_image-tracker-23/artifacts/3oojoz2e_image.png" alt="Leine Center Laatzen" />
            </div>
          </div>
        </div>
      </section>

      <section className="section section--light" id="leistungen">
        <div className="container">
          <SectionHeading
            eyebrow="Leistungskategorien"
            title="Unsere Sicherheitsdienstleistungen"
            text="Von Schutz und Sicherheit über Empfang bis Technik und Intervention – wählen Sie die passende Leistung für Ihren Bedarf."
          />

          <div className="category-grid">
            {groupedServices.map((group) => (
              <ServiceOverviewCard key={group.category} category={group.category} items={group.items} />
            ))}
          </div>
        </div>
      </section>

      <section className="section section--dark">
        <div className="container split-grid split-grid--values">
          <div>
            <SectionHeading
              eyebrow="Über Deutsche B&S"
              title="Sicherheit mit klarer Linie, ruhigem Auftreten und festen Ansprechpartnern."
              text="Deutsche Bewachung & Sicherheit unterstützt Unternehmen, Veranstalter, Wohnanlagen und sensible Einsatzorte in Hannover und Niedersachsen mit modernen, strukturierten Sicherheitsleistungen."
            />
          </div>
          <div className="value-grid">
            {VALUE_ITEMS.map((item) => (
              <article className="value-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--light trust-section">
        <div className="container">
          <div className="trust-grid">
            {REFERENCE_SECTORS.map((sector) => (
              <article className="trust-card" key={sector.title} data-testid={`reference-sector-${sector.title}`}>
                <span className="section-eyebrow">Einsatzbereich</span>
                <h3>{sector.title}</h3>
                <p>{sector.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--light-alt">
        <div className="container">
          <div className="services-grid">
            {featureServices.map((service) => (
              <ServiceCard key={service.slug} service={service} />
            ))}
          </div>
        </div>
      </section>

      <section className="section section--light">
        <div className="container split-grid">
          <div>
            <SectionHeading
              eyebrow="Ablauf"
              title="Vom Erstkontakt bis zum Einsatz – übersichtlich und ohne Umwege."
              text="Unser Fokus liegt auf einer schnellen Einschätzung, sauberer Abstimmung und einer verlässlichen Umsetzung im Einsatz."
            />
            <div className="process-list">
              {PROCESS_STEPS.map((step) => (
                <article className="process-card" key={step.title}>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>

          <InquiryForm
            type="anfrage"
            services={allServices}
            title="Schnellanfrage für Ihren Sicherheitsbedarf"
            text="Nennen Sie uns kurz Einsatzort, gewünschte Leistung und Umfang. Wir melden uns zeitnah zurück."
            compact
          />
        </div>
      </section>
    </>
  );
}

function ServicesPage({ groupedServices, allServices }) {
  return (
    <>
      <Seo
        path="/leistungen"
        title="Leistungen – Sicherheitsdienste & Objektschutz"
        description="Alle Sicherheitsleistungen der Deutsche Bewachung & Sicherheit auf einen Blick: Objektschutz, Revierdienst, Veranstaltungs- und Baustellensicherheit, Empfangs- und Telefondienste in Hannover und Niedersachsen."
        keywords="Sicherheitsleistungen, Objektschutz, Revierdienst, Eventsicherheit, Baustellenbewachung, Empfangsdienst, Hannover, Niedersachsen"
      />
      <section className="page-hero page-hero--dark">
        <div className="container page-hero__content">
          <span className="section-eyebrow">Leistungen</span>
          <h1>Alle Sicherheitsleistungen von Deutsche B&S auf einen Blick.</h1>
          <p>
            Klare Kategorien, keine Dopplungen und direkte Detailseiten für die wichtigsten Einsatzfelder in
            Hannover und Niedersachsen.
          </p>
          <div className="category-chip-row category-chip-row--dark" data-testid="services-category-row">
            {groupedServices.map((group) => (
              <span key={group.category} className="category-chip category-chip--dark" data-testid={`category-chip-${group.category}`}>
                {group.category}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--light">
        <div className="container">
          <div className="category-grid">
            {groupedServices.map((group) => (
              <ServiceOverviewCard key={group.category} category={group.category} items={group.items} />
            ))}
          </div>
        </div>
      </section>

      <section className="section section--light-alt">
        <div className="container">
          <div className="services-grid">
            {allServices.map((service) => (
              <ServiceCard key={service.slug} service={service} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function ServiceDetailPage({ allServices, siteConfig }) {
  const { slug } = useParams();
  const service = allServices.find((entry) => entry.slug === slug);

  if (!service) {
    return (
      <section className="section section--light">
        <div className="container empty-state">
          <h1>Leistung nicht gefunden</h1>
          <p>Die gewünschte Leistungsseite konnte nicht geladen werden.</p>
          <Link to="/leistungen" className="button button--primary" data-testid="back-to-services-button">
            Zur Leistungsübersicht
          </Link>
        </div>
      </section>
    );
  }

  const details = buildServiceDetails(service);
  const currentIndex = allServices.findIndex((entry) => entry.slug === slug);
  const previousService = allServices[(currentIndex - 1 + allServices.length) % allServices.length];
  const nextService = allServices[(currentIndex + 1) % allServices.length];
  const relatedServices = [1, 2, 3]
    .map((offset) => allServices[(currentIndex + offset) % allServices.length])
    .filter((entry) => entry && entry.slug !== slug)
    .slice(0, 3);
  const phoneHref = `tel:+49${siteConfig.contact.phone.replace(/^0/, "")}`;

  return (
    <>
      <Seo
        path={`/leistungen/${service.slug}`}
        title={`${service.title} – Sicherheitsdienst`}
        description={service.summary || `${service.title} durch Deutsche Bewachung & Sicherheit – professionell, zuverlässig und einsatzstark in Hannover, Niedersachsen, Bremen und Hamburg.`}
        image={service.image}
        keywords={`${service.title}, Sicherheitsdienst, ${service.category}, Hannover, Niedersachsen`}
      />
      <section className="page-hero page-hero--light detail-hero">
        <div className="container">
          <div className="detail-nav-row detail-nav-row--top">
            <Link to={`/leistungen/${previousService.slug}`} className="detail-nav-link" data-testid="detail-prev-service-link">
              <span>← {previousService.title}</span>
            </Link>
            <Link to={`/leistungen/${nextService.slug}`} className="detail-nav-link detail-nav-link--right" data-testid="detail-next-service-link">
              <span>{nextService.title} →</span>
            </Link>
          </div>
          <div className="detail-hero-grid">
            <div className="page-hero__content detail-hero__content">
              <span className="section-eyebrow">{service.category}</span>
              <h1>{service.title}</h1>
              <p>{details.lead}</p>
            </div>
            {service.image && (
              <div className="detail-hero__image">
                <img src={service.image} alt={service.title} />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="section section--light detail-layout-section">
        <div className="container detail-layout">
          <div className="detail-main">
            <article className="detail-card detail-card--wide">
              <span className="section-eyebrow">Über diese Leistung</span>
              <h2>Über diese Leistung</h2>
              <p>{service.summary}</p>
              <p>{details.suitableFor}</p>
            </article>

            <section className="detail-section-block">
              <h2>Was wir bieten</h2>
              <div className="offer-grid">
                {details.offers.map((offer) => (
                  <article className="offer-card" key={offer} data-testid={`offer-${offer}`}>
                    <CheckCircle2 size={18} />
                    <span>{offer}</span>
                  </article>
                ))}
              </div>
            </section>

            <section className="detail-section-block">
              <h2>Ihre Vorteile</h2>
              <article className="detail-card detail-card--dark detail-advantage-panel">
                <div className="advantage-grid">
                  {details.advantages.map((advantage) => (
                    <div className="advantage-item" key={advantage}>
                      <ShieldCheck size={18} />
                      <span>{advantage}</span>
                    </div>
                  ))}
                </div>
              </article>
            </section>

            <article className="detail-cta-banner">
              <div>
                <h2>{details.ctaTitle}</h2>
                <p>{details.ctaText}</p>
              </div>
              <a href="#detail-request-form" className="button button--secondary" data-testid="detail-cta-button">
                Jetzt {service.title} anfragen
              </a>
            </article>

            <section className="detail-form-wrap" id="detail-request-form">
              <InquiryForm
                type="anfrage"
                services={allServices}
                title={`Jetzt ${service.title} anfragen`}
                text="Nutzen Sie das Formular für eine strukturierte Anfrage. Wir melden uns mit einer passenden Rückmeldung bei Ihnen."
                preselectedService={service.title}
              />
            </section>

            <div className="detail-nav-row detail-nav-row--bottom">
              <Link to={`/leistungen/${previousService.slug}`} className="detail-nav-link" data-testid="detail-bottom-prev-link">
                <span>← {previousService.title}</span>
              </Link>
              <Link to={`/leistungen/${nextService.slug}`} className="detail-nav-link detail-nav-link--right" data-testid="detail-bottom-next-link">
                <span>{nextService.title} →</span>
              </Link>
            </div>
          </div>

          <aside className="detail-sidebar">
            <article className="sidebar-card">
              <h3>Schnellkontakt</h3>
              <ul className="footer-list footer-list--column">
                <li>
                  <Phone size={16} />
                  <a href={phoneHref} data-testid="detail-phone-link">
                    {siteConfig.contact.phone}
                  </a>
                </li>
                <li>
                  <Mail size={16} />
                  <a href={`mailto:${siteConfig.contact.email}`} data-testid="detail-email-link">
                    {siteConfig.contact.email}
                  </a>
                </li>
                <li>
                  <MapPin size={16} />
                  <span>
                    {siteConfig.contact.address}, {siteConfig.contact.city}
                  </span>
                </li>
              </ul>
            </article>

            <article className="sidebar-card sidebar-card--dark">
              <h3>Schnellanfrage</h3>
              <p>Unverbindlich anfragen und die passende Sicherheitsleistung strukturiert mit uns abstimmen.</p>
              <a href="#detail-request-form" className="button button--primary button--full" data-testid="detail-sidebar-cta">
                Anfrage starten
              </a>
            </article>

            <article className="sidebar-card">
              <h3>Warum wir?</h3>
              <ul className="check-list check-list--compact">
                {WHY_US_POINTS.map((point) => (
                  <li key={point}>
                    <CheckCircle2 size={18} />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </article>
          </aside>
        </div>
      </section>

      <section className="section section--light-alt">
        <div className="container">
          <div className="services-grid">
            {relatedServices.map((relatedService) => (
              <ServiceCard key={relatedService.slug} service={relatedService} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function AboutPage({ siteConfig }) {
  return (
    <>
      <Seo
        path="/ueber-uns"
        title="Über uns – Sicherheit mit Haltung"
        description="Deutsche Bewachung & Sicherheit mit Sitz in Hannover – Erfahrung, 24/7 Einsatzbereitschaft und strukturierte Sicherheitslösungen für Unternehmen, Veranstalter und Objekte."
        keywords="Sicherheitsfirma Hannover, Sicherheitsdienstleister, Deutsche Bewachung und Sicherheit, Über uns"
      />
      <section className="page-hero page-hero--dark">
        <div className="container page-hero__content">
          <span className="section-eyebrow">Über uns</span>
          <h1>Deutsche B&S steht für Sicherheit mit Haltung, Struktur und verlässlicher Kommunikation.</h1>
          <p>
            Mit Sitz in Hannover begleitet Deutsche Bewachung & Sicherheit Unternehmen, Veranstalter, Objekte und
            besondere Einsatzlagen mit modernen Sicherheitsdienstleistungen und einem seriösen Gesamtauftritt.
          </p>
        </div>
      </section>

      <section className="section section--dark about-stats-section">
        <div className="container">
          <div className="about-stats-grid about-stats-grid--two">
            <div className="about-stat">
              <span className="about-stat__number">15+</span>
              <span className="about-stat__label">Jahre Erfahrung</span>
            </div>
            <div className="about-stat">
              <span className="about-stat__number">24/7</span>
              <span className="about-stat__label">Einsatzbereitschaft</span>
            </div>
          </div>
        </div>
      </section>

      <section className="section section--light">
        <div className="container split-grid">
          <div className="about-panel">
            <SectionHeading
              eyebrow="Unser Anspruch"
              title="Nicht nur bewachen – sondern Verantwortung sichtbar übernehmen."
              text="Wir setzen auf klare Abläufe, diskrete Kommunikation und Leistungen, die sowohl Sicherheit als auch Professionalität im Außenbild stärken."
            />
            <div className="about-bullets">
              <div>
                <h3>Erfahrung</h3>
                <p>Über 15 Jahre Branchenerfahrung im Sicherheitsdienst. Bewährte Konzepte, die funktionieren.</p>
              </div>
              <div>
                <h3>Geschultes Personal</h3>
                <p>Regelmäßig fortgebildete Mitarbeiter mit §34a-Sachkunde und praxisnahen Schulungen.</p>
              </div>
              <div>
                <h3>Werte</h3>
                <p>Seriosität, Präsenz, Ruhe im Auftreten und eine saubere Abstimmung mit Auftraggebern.</p>
              </div>
              <div>
                <h3>Einsatzgebiet</h3>
                <p>Hannover und Niedersachsen – für Objekte, Baustellen, Veranstaltungen und besondere Sicherheitslagen.</p>
              </div>
              <div>
                <h3>Kommunikation</h3>
                <p>Kurze Wege per Telefon, WhatsApp, E-Mail und strukturiertem Anfrageprozess.</p>
              </div>
            </div>
          </div>
          <div className="about-contact-card">
            <h3>Standort</h3>
            <p>
              {siteConfig.contact.address}
              <br />
              {siteConfig.contact.city}
            </p>
            <h3>Kontakt</h3>
            <p>
              Telefon: {siteConfig.contact.phone}
              <br />
              E-Mail: {siteConfig.contact.email}
            </p>
            <Link to="/anfrage" className="button button--primary button--full" data-testid="about-request-button">
              Anfrage starten
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function LocationsPage({ allServices }) {
  const hannoverLocations = LOCATIONS.filter((loc) => loc.region === "Hannover");
  const otherLocations = LOCATIONS.filter((loc) => loc.region !== "Hannover");

  return (
    <>
      <Seo
        path="/standorte"
        title="Standorte – Sicherheitsdienst in Hannover, Bremen, Hamburg & mehr"
        description="Deutsche Bewachung & Sicherheit im Einsatz in allen Stadtteilen Hannovers sowie in Bremen, Hamburg, Braunschweig, Wolfsburg und Oldenburg. Finden Sie Ihren lokalen Sicherheitsdienst."
        keywords="Sicherheitsdienst Hannover Stadtteile, Sicherheitsdienst Bremen, Sicherheitsdienst Hamburg, Sicherheitsdienst Braunschweig, Sicherheitsdienst Wolfsburg, Sicherheitsdienst Oldenburg"
      />
      <section className="page-hero page-hero--dark">
        <div className="container page-hero__content">
          <span className="section-eyebrow">Einsatzgebiete</span>
          <h1>Sicherheitsdienst in Hannover, Bremen, Hamburg, Braunschweig und Wolfsburg.</h1>
          <p>
            Deutsche B&S ist in allen Stadtteilen Hannovers sowie in Bremen, Hamburg, Braunschweig, Wolfsburg und Oldenburg für Sie im Einsatz.
            Wählen Sie Ihren Standort für lokale Informationen.
          </p>
        </div>
      </section>

      <section className="section section--light">
        <div className="container">
          <SectionHeading
            eyebrow="Hannover"
            title="Alle Stadtteile in Hannover"
            text="Flächendeckende Sicherheitsdienstleistungen in allen Bezirken der Landeshauptstadt."
          />
          <div className="locations-grid">
            {hannoverLocations.map((location) => (
              <Link
                key={location.slug}
                to={`/standorte/${location.slug}`}
                className="location-card"
                data-testid={`location-card-${location.slug}`}
              >
                <MapPin size={20} className="location-card__icon" />
                <h3>{location.name}</h3>
                <p>{location.description.substring(0, 80)}...</p>
                <span className="text-link">
                  Mehr erfahren <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--light-alt">
        <div className="container">
          <SectionHeading
            title="Weitere Städte"
          />
          <div className="locations-grid locations-grid--large">
            {otherLocations.map((location) => (
              <Link
                key={location.slug}
                to={`/standorte/${location.slug}`}
                className="location-card location-card--featured"
                data-testid={`location-card-${location.slug}`}
              >
                <MapPin size={24} className="location-card__icon" />
                <h3>{location.name}</h3>
                <p>{location.description}</p>
                <div className="location-highlights">
                  {location.highlights.map((highlight) => (
                    <span key={highlight} className="location-highlight-tag">{highlight}</span>
                  ))}
                </div>
                <span className="text-link">
                  Mehr erfahren <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function LocationDetailPage({ allServices, siteConfig }) {
  const { slug } = useParams();
  const location = LOCATIONS.find((loc) => loc.slug === slug);

  if (!location) {
    return (
      <section className="section section--light">
        <div className="container empty-state">
          <h1>Standort nicht gefunden</h1>
          <p>Der gewünschte Standort konnte nicht gefunden werden.</p>
          <Link to="/standorte" className="button button--primary">
            Zur Standortübersicht
          </Link>
        </div>
      </section>
    );
  }

  const currentIndex = LOCATIONS.findIndex((loc) => loc.slug === slug);
  const prevLocation = LOCATIONS[(currentIndex - 1 + LOCATIONS.length) % LOCATIONS.length];
  const nextLocation = LOCATIONS[(currentIndex + 1) % LOCATIONS.length];
  const featuredServices = allServices.slice(0, 6);

  return (
    <>
      <Seo
        path={`/standorte/${location.slug}`}
        title={`Sicherheitsdienst ${location.name}`}
        description={`${location.description} Deutsche Bewachung & Sicherheit ist Ihr professioneller Partner in ${location.name} für Objektschutz, Revierdienst und Veranstaltungssicherheit.`}
        keywords={`Sicherheitsdienst ${location.name}, Sicherheitsfirma ${location.name}, Objektschutz ${location.name}, Bewachung ${location.name}`}
      />
      <section className="page-hero page-hero--dark location-hero">
        <div className="container">
          <div className="detail-nav-row detail-nav-row--top">
            <Link to={`/standorte/${prevLocation.slug}`} className="detail-nav-link">
              <span>← {prevLocation.name}</span>
            </Link>
            <Link to={`/standorte/${nextLocation.slug}`} className="detail-nav-link detail-nav-link--right">
              <span>{nextLocation.name} →</span>
            </Link>
          </div>
          <div className="page-hero__content">
            <span className="section-eyebrow">Sicherheitsdienst {location.region}</span>
            <h1>Sicherheitsdienst in {location.name}</h1>
            <p>{location.description}</p>
            <div className="location-highlights location-highlights--hero">
              {location.highlights.map((highlight) => (
                <span key={highlight} className="location-highlight-tag location-highlight-tag--dark">{highlight}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section section--light">
        <div className="container split-grid">
          <div>
            <SectionHeading
              eyebrow={`Sicherheit in ${location.name}`}
              title={`Unsere Leistungen für ${location.name}`}
              text={`Von Objektschutz über Revierdienst bis Veranstaltungssicherheit – Deutsche B&S bietet maßgeschneiderte Sicherheitslösungen für ${location.name} und Umgebung.`}
            />
            <div className="location-services-list">
              <div className="location-service-item">
                <ShieldCheck size={22} />
                <div>
                  <strong>Objektschutz & Werkschutz</strong>
                  <span>Zutrittskontrolle und Sicherheitspräsenz für Immobilien in {location.name}</span>
                </div>
              </div>
              <div className="location-service-item">
                <Shield size={22} />
                <div>
                  <strong>Revierdienst & Streifendienst</strong>
                  <span>Regelmäßige Kontrollfahrten und Schließdienste im Gebiet {location.name}</span>
                </div>
              </div>
              <div className="location-service-item">
                <Building2 size={22} />
                <div>
                  <strong>Baustellenbewachung</strong>
                  <span>Schutz von Baustellen und Materiallagern in {location.name}</span>
                </div>
              </div>
              <div className="location-service-item">
                <BriefcaseBusiness size={22} />
                <div>
                  <strong>Veranstaltungssicherheit</strong>
                  <span>Sicherheitsdienste für Events und Veranstaltungen in {location.name}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="location-contact-card">
            <h3>Jetzt anfragen für {location.name}</h3>
            <p>
              Kontaktieren Sie uns für ein unverbindliches Angebot für Ihren Sicherheitsbedarf in {location.name}.
            </p>
            <ul className="footer-list footer-list--column">
              <li>
                <Phone size={16} />
                <a href={`tel:+49${siteConfig.contact.phone.replace(/^0/, "")}`}>
                  {siteConfig.contact.phone}
                </a>
              </li>
              <li>
                <Mail size={16} />
                <a href={`mailto:${siteConfig.contact.email}`}>
                  {siteConfig.contact.email}
                </a>
              </li>
              <li>
                <MapPin size={16} />
                <span>{siteConfig.contact.address}, {siteConfig.contact.city}</span>
              </li>
            </ul>
            <Link to="/anfrage" className="button button--primary button--full">
              Anfrage für {location.name} starten
            </Link>
          </div>
        </div>
      </section>

      <section className="section section--light-alt">
        <div className="container">
          <SectionHeading
            eyebrow="Leistungen"
            title={`Alle Sicherheitsleistungen für ${location.name}`}
            text="Wählen Sie die passende Leistung für Ihren Bedarf."
          />
          <div className="services-grid">
            {featuredServices.map((service) => (
              <ServiceCard key={service.slug} service={service} />
            ))}
          </div>
          <div className="section-cta">
            <Link to="/leistungen" className="button button--secondary">
              Alle Leistungen anzeigen
            </Link>
          </div>
        </div>
      </section>

      <section className="section section--light">
        <div className="container">
          <SectionHeading
            eyebrow="Weitere Standorte"
            title="Deutsche B&S in anderen Gebieten"
            text="Entdecken Sie unsere Sicherheitsdienstleistungen in anderen Stadtteilen und Städten."
          />
          <div className="locations-grid">
            {LOCATIONS.filter((loc) => loc.slug !== slug).slice(0, 4).map((loc) => (
              <Link
                key={loc.slug}
                to={`/standorte/${loc.slug}`}
                className="location-card"
              >
                <MapPin size={20} className="location-card__icon" />
                <h3>{loc.name}</h3>
                <p>{loc.description.substring(0, 60)}...</p>
                <span className="text-link">
                  Mehr erfahren <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function RequestPage({ allServices }) {
  const [searchParams] = useSearchParams();
  const rawParam = searchParams.get("service") || "";
  // Resolve by slug first (e.g. ?service=revierdienst), then by title match, else pass through
  const preselectedService = useMemo(() => {
    if (!rawParam) return "";
    const bySlug = allServices.find((s) => s.slug === rawParam.toLowerCase());
    if (bySlug) return bySlug.title;
    const byTitle = allServices.find(
      (s) => s.title.toLowerCase() === rawParam.toLowerCase()
    );
    if (byTitle) return byTitle.title;
    return rawParam;
  }, [rawParam, allServices]);
  
  return (
    <>
      <Seo
        path="/anfrage"
        title="Anfrage – Sicherheitsleistung jetzt anfordern"
        description="Stellen Sie Ihre Anfrage an Deutsche Bewachung & Sicherheit. Wir beraten Sie zu Objektschutz, Revierdienst, Events und Baustellensicherheit in Hannover und Umgebung."
        keywords="Sicherheitsdienst anfragen, Angebot Sicherheitsfirma, Objektschutz anfragen"
      />
      <section className="page-hero page-hero--light">
        <div className="container page-hero__content">
          <span className="section-eyebrow">Anfrage</span>
          <h1>Teilen Sie uns Ihren Sicherheitsbedarf mit – kompakt, klar und direkt.</h1>
          <p>
            Ob Objekt, Baustelle, Veranstaltung oder individueller Schutzbedarf: Wir erfassen die wichtigsten Punkte
            strukturiert über das Anfrageformular.
          </p>
        </div>
      </section>

      <section className="section section--light-alt">
        <div className="container split-grid">
          <InquiryForm
            type="anfrage"
            services={allServices}
            title="Anfrageformular für Sicherheitsleistungen"
            text="Je genauer Ihre Angaben zu Einsatzort, Zeitraum und gewünschter Leistung sind, desto gezielter können wir reagieren."
            preselectedService={preselectedService}
          />
          <div className="contact-info-stack">
            <article className="info-card info-card--dark">
              <h3>Direktkontakt</h3>
              <p>Wenn Sie lieber direkt sprechen möchten, erreichen Sie uns zusätzlich telefonisch oder per WhatsApp.</p>
              <a href="tel:+491776957663" className="text-link" data-testid="request-phone-link">
                +49 177 695 7663 <ArrowRight size={16} />
              </a>
            </article>
            <article className="info-card">
              <h3>Typische Anfragen</h3>
              <ul className="plain-list">
                <li>Baustellenbewachung und Zugangskontrolle</li>
                <li>Empfangs- und Revierdienst</li>
                <li>Objekt- und Werkschutz</li>
                <li>Event- und VIP-Begleitung</li>
              </ul>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}

function ContactPage({ siteConfig, allServices }) {
  return (
    <>
      <Seo
        path="/kontakt"
        title="Kontakt – Sicherheitsdienst Hannover erreichen"
        description="Kontaktieren Sie Deutsche Bewachung & Sicherheit per Telefon, WhatsApp, E-Mail oder Kontaktformular. 24/7 erreichbar für Ihren Sicherheitsbedarf."
        keywords="Kontakt Sicherheitsdienst, Sicherheitsfirma anrufen, Sicherheitsdienst Hannover Kontakt"
      />
      <section className="page-hero page-hero--dark">
        <div className="container page-hero__content">
          <span className="section-eyebrow">Kontakt</span>
          <h1>Kontaktieren Sie Deutsche B&S über den Weg, der für Sie am schnellsten passt.</h1>
          <p>
            Nutzen Sie das Kontaktformular oder greifen Sie direkt zu Telefon, WhatsApp oder E-Mail.
          </p>
        </div>
      </section>

      <section className="section section--light">
        <div className="container split-grid">
          <InquiryForm
            type="kontakt"
            services={allServices}
            title="Kontaktformular"
            text="Für allgemeine Fragen, Rückrufwünsche oder erste Projektanfragen."
          />
          <div className="contact-info-stack">
            <article className="info-card">
              <h3>Kontaktdaten</h3>
              <ul className="footer-list footer-list--column">
                <li>
                  <Phone size={16} />
                  <a href={`tel:+49${siteConfig.contact.phone.replace(/^0/, "")}`} data-testid="contact-phone-link">
                    {siteConfig.contact.phone}
                  </a>
                </li>
                <li>
                  <MessageCircle size={16} />
                  <a
                    href={`https://wa.me/${formatWhatsappNumber(siteConfig.contact.whatsapp)}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-whatsapp-link"
                  >
                    WhatsApp öffnen
                  </a>
                </li>
                <li>
                  <Mail size={16} />
                  <a href={`mailto:${siteConfig.contact.email}`} data-testid="contact-email-link">
                    {siteConfig.contact.email}
                  </a>
                </li>
                <li>
                  <MapPin size={16} />
                  <span>
                    {siteConfig.contact.address}, {siteConfig.contact.city}
                  </span>
                </li>
              </ul>
            </article>
            <article className="info-card info-card--dark">
              <h3>Kurzer Weg zur Anfrage</h3>
              <p>
                Für konkrete Leistungen wie Objektschutz, Baustellenbewachung oder Personenschutz empfehlen wir das
                strukturierte Anfrageformular.
              </p>
              <Link to="/anfrage" className="button button--secondary button--full" data-testid="contact-to-request-button">
                Zum Anfrageformular
              </Link>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}




function AppRoutes({ siteConfig }) {
  const allServices = siteConfig.services || [];
  const groupedServices = useMemo(
    () =>
      CATEGORY_ORDER.map((category) => ({
        category,
        items: allServices.filter((item) => item.category === category),
      })).filter((group) => group.items.length > 0),
    [allServices],
  );

  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicLayout siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices}>
            <HomePage siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices} />
          </PublicLayout>
        }
      />
      <Route
        path="/leistungen"
        element={
          <PublicLayout siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices}>
            <ServicesPage groupedServices={groupedServices} allServices={allServices} />
          </PublicLayout>
        }
      />
      <Route
        path="/leistungen/:slug"
        element={
          <PublicLayout siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices}>
            <ServiceDetailPage allServices={allServices} siteConfig={siteConfig} />
          </PublicLayout>
        }
      />
      <Route
        path="/ueber-uns"
        element={
          <PublicLayout siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices}>
            <AboutPage siteConfig={siteConfig} />
          </PublicLayout>
        }
      />
      <Route
        path="/standorte"
        element={
          <PublicLayout siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices}>
            <LocationsPage allServices={allServices} />
          </PublicLayout>
        }
      />
      <Route
        path="/standorte/:slug"
        element={
          <PublicLayout siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices}>
            <LocationDetailPage allServices={allServices} siteConfig={siteConfig} />
          </PublicLayout>
        }
      />
      <Route
        path="/anfrage"
        element={
          <PublicLayout siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices}>
            <RequestPage allServices={allServices} />
          </PublicLayout>
        }
      />
      <Route
        path="/kontakt"
        element={
          <PublicLayout siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices}>
            <ContactPage siteConfig={siteConfig} allServices={allServices} />
          </PublicLayout>
        }
      />
      <Route
        path="/karriere"
        element={
          <PublicLayout siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices}>
            <CareerPage siteConfig={siteConfig} />
          </PublicLayout>
        }
      />
      <Route
        path="/datenschutz"
        element={
          <PublicLayout siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices}>
            <DatenschutzPage />
          </PublicLayout>
        }
      />
      <Route
        path="/impressum"
        element={
          <PublicLayout siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices}>
            <ImpressumPage />
          </PublicLayout>
        }
      />
      <Route
        path="/agb"
        element={
          <PublicLayout siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices}>
            <AGBPage />
          </PublicLayout>
        }
      />
      <Route
        path="*"
        element={
          <PublicLayout siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices}>
            <HomePage siteConfig={siteConfig} groupedServices={groupedServices} allServices={allServices} />
          </PublicLayout>
        }
      />
    </Routes>
  );
}

function App() {
  const [siteConfig, setSiteConfig] = useState(DEFAULT_SITE_CONFIG);
  const [appState, setAppState] = useState({ loading: true, error: "" });

  useEffect(() => {
    const fetchSiteConfig = async () => {
      try {
        const response = await axios.get(`${API}/site-config`);
        setSiteConfig(response.data);
        setAppState({ loading: false, error: "" });
      } catch (error) {
        setAppState({
          loading: false,
          error: extractApiError(error, "Die Inhalte konnten nicht vollständig geladen werden."),
        });
      }
    };

    fetchSiteConfig();
  }, []);

  return (
    <div className="app-shell">
      <BrowserRouter>
        <ScrollManager />
        {appState.error ? <div className="top-error-banner">{appState.error}</div> : null}
        <AppRoutes siteConfig={siteConfig} />
        {appState.loading ? <div className="loading-indicator">Inhalte werden geladen...</div> : null}
      </BrowserRouter>
    </div>
  );
}

export default App;
